import React, { Fragment } from 'react';

import {
  Checkbox,
  CheckboxGroup,
  Field,
  Grid,
  Margin,
  Select,
  SelectItem,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { URLS } from 'app/common/constants';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
} from 'app/developer-v3/hooks';

import Step from './Step';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

import type { ComplianceValues as Values } from './types';

type Props = {
  initialValues?: Values;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function Compliance({
  initialValues,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (values.companyBaseCountry === FIELD_BLANK_VALUES.companyBaseCountry) {
        errors.companyBaseCountry = 'Required.';
      }
      if (
        values.transmitsSensitiveValues ===
        FIELD_BLANK_VALUES.transmitsSensitiveValues
      ) {
        errors.transmitsSensitiveValues = 'Required.';
      }
      if (
        values.collectsFinancialInfo ===
        FIELD_BLANK_VALUES.collectsFinancialInfo
      ) {
        errors.collectsFinancialInfo = 'Required.';
      }
      if (values.usesThirdPartyApis === FIELD_BLANK_VALUES.usesThirdPartyApis) {
        errors.usesThirdPartyApis = 'Required.';
      }
      if (
        values.usesThirdPartyApis &&
        values.permissionForThirdPartyApis ===
          FIELD_BLANK_VALUES.permissionForThirdPartyApis
      ) {
        errors.permissionForThirdPartyApis = 'Required.';
      }
      if (values.usesZapierBranding === FIELD_BLANK_VALUES.usesZapierBranding) {
        errors.usesZapierBranding = 'Required.';
      }
      if (
        values.usesZapierBranding &&
        values.usesCurrentZapierBranding ===
          FIELD_BLANK_VALUES.usesCurrentZapierBranding
      ) {
        errors.usesCurrentZapierBranding = 'Required.';
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);
  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="Compliance & platform rules"
    >
      <Grid columns="1" gap="20px">
        <Field
          error={getError('companyBaseCountry', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="What country is your company based in?"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              Review this{' '}
              <Link
                href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#1-1-apps-from-a-restricted-country`}
                target="_blank"
              >
                publishing requirement
              </Link>
              .
            </Text>
          )}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="What country is your company based in?"
              name="companyBaseCountry"
              onBlur={form.onBlur}
              onChange={form.onChange}
              value={form.values.companyBaseCountry}
            />
          )}
        />
        <Select
          errorMessage={getError(
            'transmitsSensitiveValues',
            form.isTouched,
            form.errors
          )}
          helpText="The integration should not facilitate or encourage end users to transmit or receive sensitive personal data in violation of the Zapier Terms of Service."
          isDisabled={isDisabled}
          isInvalid={
            form.isTouched.transmitsSensitiveValues &&
            !!form.errors.transmitsSensitiveValues
          }
          isRequired={true}
          label="Does your integration transmit any sensitive values (e.g., passwords, tokens) via input fields?"
          onBlur={() => form.setFieldTouched('transmitsSensitiveValues', true)}
          onChange={key =>
            form.setFieldValue('transmitsSensitiveValues', key === 'true')
          }
          selectedKey={
            form.values.transmitsSensitiveValues === true
              ? 'true'
              : form.values.transmitsSensitiveValues === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        <Select
          errorMessage={getError(
            'collectsFinancialInfo',
            form.isTouched,
            form.errors
          )}
          helpText="Integrations must not facilitate any form of financial transaction, transfer of assets, or payment processing."
          isDisabled={isDisabled}
          isInvalid={
            form.isTouched.collectsFinancialInfo &&
            !!form.errors.collectsFinancialInfo
          }
          isRequired={true}
          label="Does your integration collect financial information (e.g., credit card numbers or CVV)?"
          onBlur={() => form.setFieldTouched('collectsFinancialInfo', true)}
          onChange={key =>
            form.setFieldValue('collectsFinancialInfo', key === 'true')
          }
          selectedKey={
            form.values.collectsFinancialInfo === true
              ? 'true'
              : form.values.collectsFinancialInfo === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        <Select
          errorMessage={getError(
            'usesThirdPartyApis',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isInvalid={
            form.isTouched.usesThirdPartyApis &&
            !!form.errors.usesThirdPartyApis
          }
          isRequired={true}
          label="Do you use any third-party APIs in your app/product?"
          onBlur={() => form.setFieldTouched('usesThirdPartyApis', true)}
          onChange={key =>
            form.setFieldValue('usesThirdPartyApis', key === 'true')
          }
          selectedKey={
            form.values.usesThirdPartyApis === true
              ? 'true'
              : form.values.usesThirdPartyApis === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        {form.values.usesThirdPartyApis === true && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <Field
                error={getError(
                  'permissionForThirdPartyApis',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Confirm that you have permission to use these third-party APIs in your app."
                renderHelpText={() => (
                  <Fragment>
                    <Text
                      color={isDisabled ? 'GrayWarm5' : undefined}
                      fontWeight={700}
                      tag="p"
                    >
                      Be sure to state the company that owns the API
                    </Text>
                    <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                      , and that you have permission to use this API in your
                      product. We need written confirmation from you that you
                      have permission. Review this{' '}
                      <Link
                        href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#3-1-apis`}
                        target="_blank"
                      >
                        publishing requirement
                      </Link>
                      .
                    </Text>
                  </Fragment>
                )}
                renderInput={inputProps => (
                  <TextInput
                    {...inputProps}
                    aria-label="Confirm that you have permission to use these third-party APIs in your app."
                    name="permissionForThirdPartyApis"
                    onBlur={form.onBlur}
                    onChange={form.onChange}
                    value={form.values.permissionForThirdPartyApis}
                  />
                )}
              />
            </Grid>
          </Margin>
        )}
        <Select
          errorMessage={getError(
            'usesZapierBranding',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isInvalid={
            form.isTouched.usesZapierBranding &&
            !!form.errors.usesZapierBranding
          }
          isRequired={true}
          label="Do you use Zapier's branding on your website?"
          onBlur={() => form.setFieldTouched('usesZapierBranding', true)}
          onChange={key =>
            form.setFieldValue('usesZapierBranding', key === 'true')
          }
          selectedKey={
            form.values.usesZapierBranding === true
              ? 'true'
              : form.values.usesZapierBranding === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        {form.values.usesZapierBranding === true && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <CheckboxGroup
                errorMessage={
                  getError(
                    'usesCurrentZapierBranding',
                    form.isTouched,
                    form.errors
                  ) || undefined
                }
                helpText={
                  <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                    Zapier trademarks can't be used in an integration name, but
                    they can be used in the description and partner marketing
                    material in accordance with our{' '}
                    <Link href={URLS.brand} target="_blank">
                      brand guidelines
                    </Link>
                    . Review this{' '}
                    <Link
                      href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#3-2-trademarks`}
                      target="_blank"
                    >
                      publishing requirement
                    </Link>
                    .
                  </Text>
                }
                isDisabled={isDisabled}
                isInvalid={Boolean(
                  getError(
                    'usesCurrentZapierBranding',
                    form.isTouched,
                    form.errors
                  )
                )}
                isRequired={true}
                label="Confirm you are using Zapier's most up-to-date branding."
                name="usesCurrentZapierBranding"
                onChange={keys => {
                  form.setFieldTouched('usesCurrentZapierBranding', true);
                  form.setFieldValue(
                    'usesCurrentZapierBranding',
                    keys.includes('confirmed')
                  );
                }}
                value={
                  form.values.usesCurrentZapierBranding ? ['confirmed'] : []
                }
              >
                <Checkbox value="confirmed">
                  I confirm I am using Zapier's most up-to-date branding.
                </Checkbox>
              </CheckboxGroup>
            </Grid>
          </Margin>
        )}
      </Grid>
      <Spacer height="20px" />
      <AsyncButton
        aria-label="Save compliance"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
        type="button"
        variant="secondary"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
